(function () {
  var MIN_GAP_RATIO = 0.01; // ignore sub-1% gaps, not worth painting
  var EDGE_RES = 200; // canvas resolution along the photo's edge
  var GAP_RES = 200; // canvas resolution across the box (gap axis)
  var STRIP_PX = 3; // photo rows/columns copied from each edge
  var STRIP_INSET = 1; // skip the outermost pixel row, often a JPEG/border artifact
  var ANALYSIS_RES = 200; // longest side of the downscaled copy used to inspect edges
  var EDGE_LINE_INSET = 2; // analysis-pixel line checked for a cut-off subject
  var SAME_TOLERANCE = 40; // max per-channel difference still counted as the same backdrop

  // object-fit: contain doesn't necessarily center the image — a Shopify
  // focal point sets object-position, which can push all the slack to one
  // side. Returns the fraction (0-1) of the leftover space that lands
  // before the image on each axis (0 = image flush against that edge).
  function objectPositionFractions(img) {
    var position;
    try {
      position = getComputedStyle(img).objectPosition;
    } catch (e) {
      position = '';
    }
    var parts = (position || '50% 50%').split(' ');

    function toFraction(token) {
      if (!token) return 0.5;
      if (token.indexOf('%') !== -1) return parseFloat(token) / 100;
      if (token === 'left' || token === 'top') return 0;
      if (token === 'right' || token === 'bottom') return 1;
      return 0.5; // center, or an unsupported unit (e.g. px) — don't guess
    }

    return { x: toFraction(parts[0]), y: toFraction(parts[1]) };
  }

  // Figures out which axis has a letterbox gap (object-fit: contain always
  // leaves gaps on at most one axis) and how it's split between the two
  // sides of that axis.
  function computeGap(mediaEl, img) {
    var boxW = mediaEl.clientWidth;
    var boxH = mediaEl.clientHeight;
    if (!boxW || !boxH || !img.naturalWidth || !img.naturalHeight) return null;

    var imgAspect = img.naturalWidth / img.naturalHeight;
    var boxAspect = boxW / boxH;
    var pos = objectPositionFractions(img);

    if (imgAspect > boxAspect) {
      var totalGap = (boxH - boxW / imgAspect) / boxH;
      if (totalGap < MIN_GAP_RATIO) return null;
      return { vertical: true, startRatio: totalGap * pos.y, endRatio: totalGap * (1 - pos.y) };
    }

    if (imgAspect < boxAspect) {
      var totalGapLR = (boxW - boxH * imgAspect) / boxW;
      if (totalGapLR < MIN_GAP_RATIO) return null;
      return { vertical: false, startRatio: totalGapLR * pos.x, endRatio: totalGapLR * (1 - pos.x) };
    }

    return null; // aspect ratios match — image fills the box, nothing to paint
  }

  // Downscaled pixel grid of the whole photo, used to judge whether its
  // edges are plain backdrop or the subject running off the frame.
  function readPixels(probe) {
    var scale = Math.min(1, ANALYSIS_RES / Math.max(probe.naturalWidth, probe.naturalHeight));
    var aw = Math.max(1, Math.round(probe.naturalWidth * scale));
    var ah = Math.max(1, Math.round(probe.naturalHeight * scale));
    var canvas = document.createElement('canvas');
    canvas.width = aw;
    canvas.height = ah;
    var ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(probe, 0, 0, aw, ah);
    var data = ctx.getImageData(0, 0, aw, ah).data; // throws if tainted
    return {
      w: aw,
      h: ah,
      at: function (x, y) {
        var i = (y * aw + x) * 4;
        return [data[i], data[i + 1], data[i + 2], data[i + 3]];
      },
    };
  }

  // Transparent pixels match each other; opaque ones match when no channel
  // differs by more than SAME_TOLERANCE (absorbs JPEG noise and the soft
  // falloff of a studio backdrop).
  function samePixel(p, q) {
    var pClear = p[3] < 200;
    var qClear = q[3] < 200;
    if (pClear || qClear) return pClear && qClear;
    return (
      Math.abs(p[0] - q[0]) <= SAME_TOLERANCE &&
      Math.abs(p[1] - q[1]) <= SAME_TOLERANCE &&
      Math.abs(p[2] - q[2]) <= SAME_TOLERANCE
    );
  }

  // Walks a line of pixels next to one edge of the photo (inset to skip
  // border artifacts). A backdrop — even a vignette — changes smoothly along
  // it; the subject cut off by the frame shows up as a sharp jump.
  function edgeIsBusy(px, edge) {
    var vertical = edge === 'top' || edge === 'bottom';
    var len = vertical ? px.w : px.h;
    var depth = vertical ? px.h : px.w;
    var line = Math.min(EDGE_LINE_INSET, depth - 1);
    if (edge === 'bottom' || edge === 'right') line = depth - 1 - line;
    for (var i = 0; i + 2 < len; i++) {
      var a = vertical ? px.at(i, line) : px.at(line, i);
      var b = vertical ? px.at(i + 2, line) : px.at(line, i + 2);
      if (!samePixel(a, b)) return true;
    }
    return false;
  }

  // True when zooming to object-fit: cover would only trim plain backdrop:
  // every pixel in the band that would be cut matches the outermost pixel
  // of its row/column.
  function zoomTrimsOnlyBackdrop(px, gap, pos, mediaEl, img) {
    var imgAspect = img.naturalWidth / img.naturalHeight;
    var boxAspect = mediaEl.clientWidth / mediaEl.clientHeight;
    // Fraction of the photo cover would cut on the axis without the gap.
    var trim = gap.vertical ? 1 - boxAspect / imgAspect : 1 - imgAspect / boxAspect;
    var frac = gap.vertical ? pos.x : pos.y;
    var len = gap.vertical ? px.w : px.h;
    var cross = gap.vertical ? px.h : px.w;
    var startBand = Math.min(len, Math.ceil(trim * frac * len) + 1);
    var endBand = Math.min(len, Math.ceil(trim * (1 - frac) * len) + 1);
    var mismatches = 0;
    var allowed = Math.max(2, (startBand + endBand) * cross * 0.005);

    for (var c = 0; c < cross; c++) {
      var first = gap.vertical ? px.at(0, c) : px.at(c, 0);
      var last = gap.vertical ? px.at(len - 1, c) : px.at(c, len - 1);
      for (var i = 0; i < startBand; i++) {
        if (!samePixel(gap.vertical ? px.at(i, c) : px.at(c, i), first)) mismatches++;
      }
      for (var j = len - endBand; j < len; j++) {
        if (!samePixel(gap.vertical ? px.at(j, c) : px.at(c, j), last)) mismatches++;
      }
      if (mismatches > allowed) return false;
    }
    return true;
  }

  // Flat fill for a gap whose edge is the subject: the backdrop color at the
  // two corners of that edge, or null (scheme --image_bg) if they disagree
  // or are transparent.
  function cornerColor(px, edge) {
    var a, b;
    if (edge === 'top') { a = px.at(0, 0); b = px.at(px.w - 1, 0); }
    else if (edge === 'bottom') { a = px.at(0, px.h - 1); b = px.at(px.w - 1, px.h - 1); }
    else if (edge === 'left') { a = px.at(0, 0); b = px.at(0, px.h - 1); }
    else { a = px.at(px.w - 1, 0); b = px.at(px.w - 1, px.h - 1); }
    if (a[3] < 200 || b[3] < 200 || !samePixel(a, b)) return null;
    return 'rgb(' + Math.round((a[0] + b[0]) / 2) + ',' + Math.round((a[1] + b[1]) / 2) + ',' + Math.round((a[2] + b[2]) / 2) + ')';
  }

  // Fills the letterbox gap. Plain-backdrop edges are stretched outward so a
  // vignette or gradient continues past the photo. Where the subject runs
  // off the frame (a model's head or legs), stretching would smear it, so
  // the photo is zoomed to fill the box if that only trims backdrop, or
  // that side gets the backdrop's flat color instead.
  function applySampledBackground(mediaEl, img, key) {
    mediaEl.style.backgroundImage = '';
    img.style.objectFit = '';
    var gap = computeGap(mediaEl, img);
    if (!gap) return;

    // The card <img> isn't loaded with crossorigin="anonymous", so drawing
    // it would taint the canvas. A CORS probe of the same URL reads cleanly.
    var probe = new Image();
    probe.crossOrigin = 'anonymous';
    probe.onload = function () {
      // A swatch may have swapped the photo while this probe was loading.
      if (mediaEl.dataset.bgSrc !== key) return;
      var w = probe.naturalWidth;
      var h = probe.naturalHeight;
      if (!w || !h) return;

      var px;
      try {
        px = readPixels(probe);
      } catch (e) {
        return; // tainted canvas — keep the theme's default --image_bg
      }
      if (!px) return;

      var startEdge = gap.vertical ? 'top' : 'left';
      var endEdge = gap.vertical ? 'bottom' : 'right';
      var startBusy = edgeIsBusy(px, startEdge);
      var endBusy = edgeIsBusy(px, endEdge);

      if ((startBusy || endBusy) && zoomTrimsOnlyBackdrop(px, gap, objectPositionFractions(img), mediaEl, img)) {
        img.style.objectFit = 'cover';
        return;
      }

      var canvas = document.createElement('canvas');
      canvas.width = gap.vertical ? EDGE_RES : GAP_RES;
      canvas.height = gap.vertical ? GAP_RES : EDGE_RES;
      var ctx = canvas.getContext('2d');
      if (!ctx) return;

      // +1 overlaps into the area the photo covers, so no hairline seam
      // shows at the boundary from rounding.
      var startPx = Math.ceil(gap.startRatio * GAP_RES) + 1;
      var endPx = Math.ceil(gap.endRatio * GAP_RES) + 1;
      var span = gap.vertical ? h : w;
      var strip = Math.max(1, Math.min(STRIP_PX, span - 2 * STRIP_INSET));
      var inset = span > strip + 2 * STRIP_INSET ? STRIP_INSET : 0;
      var farOffset = span - inset - strip;

      function paintFlat(edge, x, y, fw, fh) {
        var color = cornerColor(px, edge);
        if (!color) return; // leave transparent — scheme --image_bg shows
        ctx.fillStyle = color;
        ctx.fillRect(x, y, fw, fh);
      }

      if (gap.vertical) {
        if (startBusy) paintFlat('top', 0, 0, EDGE_RES, startPx);
        else ctx.drawImage(probe, 0, inset, w, strip, 0, 0, EDGE_RES, startPx);
        if (endBusy) paintFlat('bottom', 0, GAP_RES - endPx, EDGE_RES, endPx);
        else ctx.drawImage(probe, 0, farOffset, w, strip, 0, GAP_RES - endPx, EDGE_RES, endPx);
      } else {
        if (startBusy) paintFlat('left', 0, 0, startPx, EDGE_RES);
        else ctx.drawImage(probe, inset, 0, strip, h, 0, 0, startPx, EDGE_RES);
        if (endBusy) paintFlat('right', GAP_RES - endPx, 0, endPx, EDGE_RES);
        else ctx.drawImage(probe, farOffset, 0, strip, h, GAP_RES - endPx, 0, endPx, EDGE_RES);
      }

      var url;
      try {
        url = canvas.toDataURL('image/png');
      } catch (e) {
        return; // tainted canvas — keep the theme's default --image_bg
      }

      // Transparent PNG edges stay transparent, so the scheme's --image_bg
      // still shows through for cutout product shots.
      mediaEl.style.backgroundImage = 'url("' + url + '")';
      mediaEl.style.backgroundSize = '100% 100%';
      mediaEl.style.backgroundRepeat = 'no-repeat';
    };
    probe.src = img.currentSrc || img.src;
  }

  // Identifies the photo file, ignoring ?width= so lazysizes swapping in a
  // sharper copy of the same photo doesn't trigger a repaint, while a
  // swatch switching to another variant's photo does.
  function photoKey(img) {
    return (img.currentSrc || img.src || '').split('?')[0];
  }

  function sampleCard(img) {
    var mediaEl = img.closest('.media');
    if (!mediaEl || !img.naturalWidth) return;
    var key = photoKey(img);
    if (!key || mediaEl.dataset.bgSrc === key) return;

    function attempt() {
      if (mediaEl.dataset.bgSrc === key) return true;
      // Layout can lag the image's load event; don't give up permanently
      // on a zero-size reading or the card never gets a background.
      if (!mediaEl.clientWidth || !mediaEl.clientHeight) return false;
      mediaEl.dataset.bgSrc = key;
      applySampledBackground(mediaEl, img, key);
      return true;
    }

    if (mediaEl.bgResizeObserver) {
      mediaEl.bgResizeObserver.disconnect();
      mediaEl.bgResizeObserver = null;
    }
    if (attempt()) return;

    var resizeObserver = new ResizeObserver(function () {
      if (attempt()) {
        resizeObserver.disconnect();
        mediaEl.bgResizeObserver = null;
      }
    });
    mediaEl.bgResizeObserver = resizeObserver;
    resizeObserver.observe(mediaEl);
  }

  function bindImage(img) {
    if (img.dataset.bgListening) return;
    img.dataset.bgListening = 'true';
    // Not once: swatches swap srcset on the same <img> (theme.js variant
    // change), and each new photo needs its own fill.
    img.addEventListener('load', function () {
      sampleCard(img);
    });
    if (img.complete && img.naturalWidth) sampleCard(img);
  }

  function initCardBackgrounds(root) {
    if (root.matches && root.matches('img.product--card-image')) bindImage(root);
    if (root.querySelectorAll) root.querySelectorAll('img.product--card-image').forEach(bindImage);
  }

  function init() {
    initCardBackgrounds(document);

    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType === 1) initCardBackgrounds(node);
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
